import { numberOfOffers } from '@/infrastructure/Configuration'
import { DateFactory } from 'rhg-vue-common'
import { formatDate } from '@/infrastructure/Utils'

export default class OffersFilter {
  static empty() {
    return new OffersFilter({})
  }

  static initFromUrl(allData, filter, queryParams) {
    const selectedBrands = allData.brands
      ? allData.brands.filter(brand =>
        (queryParams.brand || '').includes(brand.code)
      )
      : []

    const selectedCategory = allData.categories
      ? allData.categories.filter(category =>
        (queryParams.category || '').includes(category.code)
      )
      : []

    const selectedRegion = queryParams.region && allData.regions
      ? allData.regions.find(region =>
        (queryParams.region || '').includes(region.code)
      )
      : undefined

    const selectedCountry = queryParams.country && allData.countries
      ? allData.countries.filter(country =>
        (queryParams.country.toUpperCase() || '').includes(country.code)
      )
      : []

    const selectedCity = queryParams.city && allData.countries
      ? allData.countries.reduce((result, country) => {
        const sCity = country.cities.find(city => city.code === queryParams.city)
        if (sCity) {
          result.push(sCity)
        }
        return result
      }, [])
      : []

    const dateRange = {
      start: queryParams.startDate ? DateFactory.dateObject(queryParams.startDate) : '',
      end: queryParams.endDate ? DateFactory.dateObject(queryParams.endDate) : ''
    }

    return filter.addBrands(selectedBrands)
      .addCategories(selectedCategory)
      .addCountry(selectedCountry[0])
      .addCity(selectedCity[0])
      .addRegion(selectedRegion)
      .changeDateRange(dateRange)
      .addLocation({ latitude: queryParams.latitude, longitude: queryParams.longitude, distance: queryParams.distance })
      .rewardsOnly(queryParams.isRewardsOnly === 'true')
  }

  constructor({
    categories = [],
    countries = [],
    cities = [],
    startDate = null,
    endDate = null,
    brands = [],
    isRewardsOnly = false,
    region = '',
    latitude = null,
    longitude = null,
    distance = null,
    offset = 0,
    codes = [],
    tag = [],
    globalCampaign = false,
    dna = ''
  }) {
    this.categories = categories
    this.countries = countries
    this.cities = cities
    this.startDate = startDate ? formatDate(startDate) : null
    this.endDate = endDate ? formatDate(endDate) : null
    this.brands = brands
    this.region = region
    this.isRewardsOnly = isRewardsOnly
    this.offset = offset
    this.latitude = latitude
    this.longitude = longitude
    this.distance = distance
    this.codes = codes
    this.tag = tag
    this.globalCampaign = globalCampaign
    this.dna = dna
  }

  isEmpty = () => !this.nonEmpty()
  nonEmpty = () =>
    this.hasCategories() ||
    this.hasCountries() ||
    this.hasCities() ||
    this.hasTags() ||
    this.hasBrands() ||
    this.hasDates() ||
    this.hasRegion() ||
    this.hasGeoLocation() ||
    this.hasCodes() ||
    this.isRewardsOnly

  hasCategories = () => this.categories.length > 0
  hasCountries = () => this.countries.length > 0
  hasCities = () => this.cities.length > 0
  hasTags = () => this.tag && this.tag.length > 0
  hasBrands = () => this.brands.length > 0
  hasRegion = () => this.region.length > 0
  hasGeoLocation = () => this.latitude && this.longitude && this.distance
  hasDates = () => !!this.startDate && !!this.endDate
  hasFilterWithoutRewardOnly = () =>
    this.hasCategories() || this.hasCountries() || this.hasBrands() || this.hasDates()

  hasCodes = () => this.codes.length > 0
  lengthOfStay = () => (this.hasDates() ? DateFactory.daysBetween(this.endDate, this.startDate) : 0)

  addSingleCategory = categoryToAdd => {
    return this.copy({ categories: [categoryToAdd.code], offset: 0 })
  }

  addCategory = categoryToAdd =>
    this.copy({ categories: [...new Set(this.categories.concat(categoryToAdd.code))], offset: 0 })

  addRegion = regionToAdd => {
    if (regionToAdd && regionToAdd.length !== 0) {
      return this.copy({ region: regionToAdd.code, offset: 0 })
    } else {
      return this.copy({ offset: 0 })
    }
  }

  addLocation = locationToAdd => this.copy({ latitude: locationToAdd.latitude, longitude: locationToAdd.longitude, distance: locationToAdd.distance, offset: 0 })

  addCategories = categoriesToAdd => {
    return this.copy({
      categories: categoriesToAdd.reduce((acc, category) => acc.concat(category.code), []),
      offset: 0
    })
  }

  clearLocationFilters = () => this.copy({ latitude: null, longitude: null, distance: null, region: '', cities: [], countries: [], offset: 0 })

  removeCategory = categoryToRemove =>
    this.copy({
      categories: this.categories.filter(category => category !== categoryToRemove.code),
      offset: 0
    })

  removeCategories = categoriesToRemove => {
    const cs = categoriesToRemove.map(c => c.code)
    const newCategories = this.categories.filter(category => !cs.includes(category))

    return this.copy({ categories: newCategories })
  }

  setFirstPage() {
    return this.copy({ offset: 0 })
  }

  addCountry = countryToAdd => {
    if (countryToAdd) {
      return this.copy({ countries: [countryToAdd.code], cities: [], offset: 0 })
    } else {
      return this.copy({ countries: [], cities: [], offset: 0 })
    }
  }

  addCity = cityToAdd => {
    if (cityToAdd) {
      return this.copy({ cities: [cityToAdd.code], offset: 0 })
    } else {
      return this.copy({ cities: [], offset: 0 })
    }
  }

  changeDateRange = dateRange => {
    const startDate = dateRange.start ? formatDate(dateRange.start) : null
    const endDate = dateRange.end ? formatDate(dateRange.end) : null
    return this.copy({
      startDate,
      endDate,
      offset: 0
    })
  }

  addBrand = brandToAdd => {
    return this.copy({ brands: [...new Set(this.brands.concat(brandToAdd.code))], offset: 0 })
  }

  addBrands = brandsToAdd => {
    return this.copy({
      brands: brandsToAdd.reduce((acc, brand) => acc.concat(brand.code), []),
      offset: 0
    })
  }

  removeBrand = brandToRemove =>
    this.copy({ brands: this.brands.filter(brand => brand !== brandToRemove.code), offset: 0 })

  removeBrands = brandsToRemove => {
    const bs = brandsToRemove.map(b => b.code)
    const newBrands = this.brands.filter(brand => !bs.includes(brand))

    return this.copy({ brands: newBrands })
  }

  toggleRewardsOnly = () => this.copy({ isRewardsOnly: !this.isRewardsOnly, offset: 0 })
  rewardsOnly = value => this.copy({ isRewardsOnly: value, offset: 0 })

  next = () => this.copy({ offset: this.offset + 1 })

  asQueryParams = () => {
    const params = {
      sort: ['multi', 'registration', 'experience', 'single'],
      limit: numberOfOffers,
      offset: this.offset,
      category: this.categories,
      country: this.countries,
      city: this.cities,
      brand: this.brands,
      all: true,
      geotargetCountryCode: global.AkamaiService.userCountry(),
      fields: [
        'offers.title',
        'offers.cardSubtitle',
        'offers.mainImage',
        'offers.started',
        'offers.reservationStartDate',
        'offers.reservationEndDate',
        'offers.corporateId',
        'offers.pacCode',
        'offers.bookingStartDate',
        'offers.bookingEndDate',
        'offers.categories',
        'offers.cities',
        'offers.brands',
        'offers.rewards',
        'offers.detailPageUrl',
        'offers.discount',
        'offers.dna',
        'total',
        'categories',
        'brands',
        'countries',
        'cities',
        'dnas'
      ]
    }

    if (this.tag) {
      params.tag = this.tag
    }

    if (this.globalCampaign) {
      params.globalCampaign = false
    }
    const sortOnLogged = ['registration', 'multi', 'experience', 'single']
    if (global.AuthDispatcher.isLogged()) {
      params.sort = sortOnLogged
    }
    if (this.dna) {
      params.sort = [this.dna, ...sortOnLogged]
    }
    if (this.isRewardsOnly) {
      params.rewards = true
    }

    if (this.longitude && this.latitude && this.distance) {
      params.longitude = this.longitude
      params.latitude = this.latitude
      params.distance = this.distance
    }

    if (this.region) {
      params.region = this.region
    }

    if (this.startDate && this.endDate) {
      params.bookingStartDate = DateFactory.formatDate(this.startDate)
      params.bookingEndDate = DateFactory.formatDate(this.endDate)
    }
    if (this.codes.length > 0) {
      params.code = this.codes
    }
    // eslint-disable-next-line no-undef
    return global.$.param(params).replace(/%5B%5D/gi, '')
  }

  asUrlParams = () => {
    return {
      ...(this.categories.length !== 0) && { category: this.categories[0] },
      ...(this.countries.length === 1 && { country: this.countries[0] }),
      ...(this.cities.length !== 0) && { city: this.cities[0] },
      ...(this.startDate) && { startDate: this.startDate },
      ...(this.endDate) && { endDate: this.endDate },
      ...(this.brands.length !== 0) && { brand: this.brands[0] },
      ...(this.isRewardsOnly) && { isRewardsOnly: this.isRewardsOnly },
      ...(this.region) && { region: this.region },
      ...(this.latitude) && { latitude: this.latitude },
      ...(this.longitude) && { longitude: this.longitude },
      ...(this.distance) && { distance: this.distance }
    }
  }

  copy = partial =>
    new OffersFilter({
      categories: this.categories,
      countries: this.countries,
      cities: this.cities,
      startDate: this.startDate,
      endDate: this.endDate,
      brands: this.brands,
      isRewardsOnly: this.isRewardsOnly,
      offset: this.offset,
      region: this.region,
      latitude: this.latitude,
      longitude: this.longitude,
      distance: this.distance,
      codes: this.codes,
      ...partial
    })
}
